import { useState, useMemo } from "react";
import { useToast } from "@/components/ui/use-toast";
import { EventSessionAttendanceItem } from "@/modules/eventRegistration/features/EventWaitlist/api/fetchEventAttendences/types";
import { WaitlistDetails } from "@/modules/eventRegistration/features/EventWaitlist/api/fetchWaitlistDetails/types";

interface UseAttendanceProps {
  attendances: EventSessionAttendanceItem[];
  updateAttendance: (
    attendanceId: number,
    data: { statusId?: number; notes?: string },
  ) => Promise<void>;
  bulkUpdateAttendanceStatus: (
    attendanceIds: number[],
    statusId: number,
  ) => Promise<void>;
  waitlistDetails: WaitlistDetails | null;
}

export default function useAttendance({
  attendances,
  updateAttendance,
  bulkUpdateAttendanceStatus,
  waitlistDetails,
}: UseAttendanceProps) {
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [selectedAttendance, setSelectedAttendance] =
    useState<EventSessionAttendanceItem | null>(null);
  const [selectedAttendanceIds, setSelectedAttendanceIds] = useState<
    Set<number>
  >(new Set());
  const [bulkStatus, setBulkStatus] = useState<string>("");

  const { toast } = useToast();

  const isAllSelected = useMemo(() => {
    return (
      attendances.length > 0 &&
      selectedAttendanceIds.size === attendances.length
    );
  }, [attendances.length, selectedAttendanceIds.size]);

  const hasSelectedItems = useMemo(() => {
    return selectedAttendanceIds.size > 0;
  }, [selectedAttendanceIds.size]);

  const handleSelectAll = () => {
    if (isAllSelected) {
      setSelectedAttendanceIds(new Set());
    } else {
      setSelectedAttendanceIds(new Set(attendances.map((a) => a.id)));
    }
  };

  const handleSelectAttendance = (attendanceId: number, checked: boolean) => {
    const newSelected = new Set(selectedAttendanceIds);
    if (checked) {
      newSelected.add(attendanceId);
    } else {
      newSelected.delete(attendanceId);
    }
    setSelectedAttendanceIds(newSelected);
  };

  const getStatusIdByName = (statusName: string): number | null => {
    // First, try to get from waitlistDetails if available
    if (waitlistDetails?.eventSessionAttendanceStatuses) {
      const status = waitlistDetails.eventSessionAttendanceStatuses.find(
        (s) => s.name.toLowerCase() === statusName.toLowerCase(),
      );
      if (status) {
        return status.id;
      }
    }

    // Fallback to searching in current attendance records
    for (const attendance of attendances) {
      if (
        attendance.status &&
        attendance.status.name.toLowerCase() === statusName.toLowerCase()
      ) {
        return attendance.status.id;
      }
    }
    return null;
  };

  const handleBulkStatusChange = async (status: string) => {
    if (selectedAttendanceIds.size === 0) {
      return;
    }

    const statusId = getStatusIdByName(status);
    if (!statusId) {
      toast({
        title: "Error",
        description: `Status ID not found for status: ${status}`,
        variant: "destructive",
      });
      return;
    }

    const attendanceIds = Array.from(selectedAttendanceIds);

    try {
      await bulkUpdateAttendanceStatus(attendanceIds, statusId);
      setSelectedAttendanceIds(new Set());
      setBulkStatus("");
    } catch (error) {
      console.error("Error bulk updating attendance status:", error);
      const message =
        error instanceof Error
          ? error.message
          : "Failed to update attendance records.";
      toast({
        title: "Error",
        description: message,
        variant: "destructive",
      });
    }
  };

  const handleNotesClick = (attendance: EventSessionAttendanceItem) => {
    setSelectedAttendance(attendance);
    setShowNotesModal(true);
  };

  const handleSaveNotes = async (notes: string) => {
    if (!selectedAttendance?.id) return;

    try {
      await updateAttendance(selectedAttendance.id, { notes });
      setShowNotesModal(false);
    } catch (error) {
      console.error("Error saving notes:", error);
      const message =
        error instanceof Error ? error.message : "Failed to save notes.";
      toast({
        title: "Error",
        description: message,
        variant: "destructive",
      });
    }
  };

  const handleStatusUpdate = async (attendanceId: number, status: string) => {
    const statusId = getStatusIdByName(status);
    if (!statusId) {
      toast({
        title: "Error",
        description: `Status ID not found for status: ${status}`,
        variant: "destructive",
      });
      return;
    }

    try {
      await updateAttendance(attendanceId, { statusId });
    } catch (error) {
      console.error("Error updating attendance status:", error);
      const message =
        error instanceof Error
          ? error.message
          : "Failed to update attendance status.";
      toast({
        title: "Error",
        description: message,
        variant: "destructive",
      });
    }
  };

  return {
    // State
    showNotesModal,
    selectedAttendance,
    selectedAttendanceIds,
    bulkStatus,
    isAllSelected,
    hasSelectedItems,
    handleSelectAll,
    handleSelectAttendance,
    handleBulkStatusChange,
    handleNotesClick,
    handleSaveNotes,
    handleStatusUpdate,
    setShowNotesModal,
    setBulkStatus,
  };
}
